import React, { useCallback, useEffect, useMemo, useState } from 'react';

const CSS = require('./TimeInput.module.scss');

const generateHourOptions = () => {
  const hours = [];
  const limit = 23;
  for (let i = 0; i <= limit; i++) {
    hours.push(i.toString().padStart(2, '0'));
  }
  return hours;
};

const generateMinuteOptions = (step: number) => {
  const minutes: string[] = [];
  for (let minute = 0; minute < 60; minute += step) {
    minutes.push(minute.toString().padStart(2, '0'));
  }
  return minutes;
};

interface TimePicker24HoursProps {
  defaultValue: {
    hour: string;
    minute: string;
  };
  onChange?: (value: string) => void;
  disabled?: boolean;
  minuteStep?: number;
}

function TimePicker24Hours({
  defaultValue,
  onChange,
  disabled,
  minuteStep = 30,
}: TimePicker24HoursProps) {
  const [hour, setHour] = useState(defaultValue.hour);
  const [minute, setMinute] = useState(defaultValue.minute);

  const hourOptions = generateHourOptions();
  const emitChange = useCallback(
    (nextHour: string, nextMinute: string) => {
      onChange?.(`${nextHour}:${nextMinute}`);
    },
    [onChange],
  );

  const minuteOptions = useMemo(
    () => generateMinuteOptions(minuteStep),
    [minuteStep],
  );

  useEffect(() => {
    setHour(defaultValue.hour);
    const normalizedMinute = minuteOptions.includes(defaultValue.minute)
      ? defaultValue.minute
      : minuteOptions[0];
    setMinute(normalizedMinute);
    if (normalizedMinute !== defaultValue.minute) {
      emitChange(defaultValue.hour, normalizedMinute);
    }
  }, [defaultValue, minuteOptions, emitChange]);

  const handleHourChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextHour = event.target.value;
    setHour(nextHour);
    emitChange(nextHour, minute);
  };

  const handleMinuteChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextMinute = event.target.value;
    setMinute(nextMinute);
    emitChange(hour, nextMinute);
  };

  return (
    <>
      <div className={CSS.TimeInput}>
        <select
          value={hour}
          onChange={handleHourChange}
          className={CSS.TimeSelect}
          disabled={disabled}
        >
          {hourOptions.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
        <span>:</span>
        <select
          value={minute}
          onChange={handleMinuteChange}
          className={CSS.TimeSelect}
          disabled={disabled}
        >
          {minuteOptions.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </div>
      <input type="hidden" value={`${hour}:${minute}`} name="time" />
    </>
  );
}

export default TimePicker24Hours;
